/**
 * **Le calage audio et son mini-test** — SPEC-UI §7.7 : « réglage "calage audio" (−200 à
 * +200 ms, pas de 5 ms) dans les paramètres, avec un mini-test : 8 beats à 90 BPM, taper Espace,
 * l'offset médian mesuré est proposé comme valeur ».
 *
 * Le champ existe depuis M2.29 ; sa valeur est LUE par le Montage depuis ⟦A-76⟧ (les frappes
 * sont corrigées à la capture). Il manquait le contrôle — le curseur était désactivé — et le
 * test. Les voici.
 *
 * ## Comment le test mesure
 *
 * Huit temps sont programmés sur `performance.now()` — le même repère que `useRafLoop` et que
 * les frappes du Montage — à 666,67 ms d'intervalle, après une seconde d'attente. Chaque temps
 * fait tinter `cut_hit` (le SFX du Montage, pour que l'oreille entende ce qu'elle entendra en
 * jeu) et pulse un point à l'écran. Espace capte `performance.now()` ; à la fin, `offsetPropose`
 * (pur, testé) rend la médiane signée des écarts — et le joueur **choisit** de l'appliquer.
 *
 * ⚠️ Le SFX part par `setTimeout` : quelques millisecondes de gigue, que la médiane absorbe.
 *
 * ⟦C50 · A-169 §2⟧ **Le test mesure ce que la compensation automatique ne couvre pas.** La musique du
 * Montage est avancée de la latence de sortie mesurée sur la carte son (`audio/latence.ts` : 176 ms sur un
 * casque Bluetooth) pour être ENTENDUE à l'instant des carrés. Le temps du test est donc, lui aussi, joué
 * avec cette avance : le son est commandé `latence` ms avant l'instant du temps, il est ENTENDU à cet
 * instant, et la médiane ne retient que le réflexe et l'entrée du joueur. Sans cette avance, le décalage
 * proposé contiendrait la latence de sortie — déjà compensée en jeu — et l'appliquer la compterait deux fois.
 * (Le pouls visuel, lui, bat à l'instant du temps : c'est celui que l'œil voit.)
 *
 * ⟦C57 · lot 5c · A-174⟧ **Des mots isolés pendant le test, une phrase pour le résultat.** Le joueur a les
 * yeux sur le point qui bat, pas sur un paragraphe : « Prêt… », « Temps 3/8 », « Tape Espace ». Le bilan tient
 * en une phrase (l'écart, le décalage proposé). La consigne complète (« 8 temps à 90 BPM, tape Espace sur
 * chaque temps ») vit dans les dessous du groupe « Son et calage » (`Reglages.tsx`) ; le nombre de frappes utiles
 * reste en petit sous le bilan.
 */

import { useEffect, useRef, useState } from "react";

import { useMusique, useSfx } from "../../components/SoundProvider";
import { useUIStore } from "../../stores/uiStore";

import {
  BEATS_TEST,
  BPM_TEST,
  FRAPPES_MIN,
  PERIODE_TEST_MS,
  offsetPropose,
  tempsDuTest,
  type MesureCalage,
} from "../../audio/calage";

type Etat = "repos" | "en_cours" | "fini";

export function CalageAudio() {
  const sfx = useSfx();
  const musique = useMusique();
  const setSetting = useUIStore((s) => s.setSetting);
  const [etat, setEtat] = useState<Etat>("repos");
  const [temps, setTemps] = useState(0);
  const [frappes, setFrappes] = useState(0);
  const [mesure, setMesure] = useState<MesureCalage | null | "insuffisant">(null);
  const depart = useRef(0);
  const frappesRef = useRef<number[]>([]);
  const minuteries = useRef<number[]>([]);

  function arreter() {
    for (const m of minuteries.current) window.clearTimeout(m);
    minuteries.current = [];
  }

  function lancer() {
    arreter();
    frappesRef.current = [];
    setFrappes(0);
    setTemps(0);
    setMesure(null);
    setEtat("en_cours");
    depart.current = performance.now();
    const grille = tempsDuTest();
    // ⟦C50⟧ La latence de sortie de la carte son, mesurée à l'instant du lancement (0 si le navigateur ne la dit pas).
    const latenceMs = musique?.mesureLatence().ms ?? 0;
    grille.forEach((t, i) => {
      minuteries.current.push(
        window.setTimeout(() => sfx("cut_hit"), Math.max(0, t - latenceMs)),
        window.setTimeout(() => setTemps(i + 1), t),
      );
    });
    // Une période après le dernier temps : on ferme et on mesure.
    minuteries.current.push(
      window.setTimeout(() => {
        const m = offsetPropose(grille, frappesRef.current);
        setMesure(m ?? "insuffisant");
        setEtat("fini");
      }, grille[grille.length - 1]! + PERIODE_TEST_MS),
    );
  }

  useEffect(() => {
    if (etat !== "en_cours") return;
    const auClavier = (e: KeyboardEvent) => {
      if (e.code !== "Space") return;
      e.preventDefault();
      frappesRef.current.push(performance.now() - depart.current);
      setFrappes(frappesRef.current.length);
    };
    window.addEventListener("keydown", auClavier);
    return () => window.removeEventListener("keydown", auClavier);
  }, [etat]);

  useEffect(() => () => arreter(), []);

  return (
    <div className="calage-test">
      {etat === "repos" ? (
        <button type="button" className="bouton discret" onClick={lancer}>
          🥁 Tester : {BEATS_TEST} temps à {BPM_TEST} BPM
        </button>
      ) : null}

      {etat === "en_cours" ? (
        <div className="calage-en-cours" aria-live="polite">
          <span className={`calage-pulse ${temps > 0 ? "bat" : ""}`} key={temps} aria-hidden="true" />
          <span>
            {temps === 0
              ? "Prêt…"
              : `Temps ${temps}/${BEATS_TEST} · ${frappes} frappe${frappes > 1 ? "s" : ""}`}
          </span>
          <span className="reglage-aide">Tape Espace à chaque temps</span>
        </div>
      ) : null}

      {etat === "fini" ? (
        <div className="calage-resultat">
          {mesure === "insuffisant" || mesure === null ? (
            <p className="reglage-aide">
              Trop peu de frappes ({FRAPPES_MIN} au moins) : réessaie.
            </p>
          ) : (
            <>
              <p>
                Tu frappes{" "}
                <strong>
                  {mesure.medianeMs > 0 ? `${mesure.medianeMs} ms après` : mesure.medianeMs < 0 ? `${Math.abs(mesure.medianeMs)} ms avant` : "pile sur"}
                </strong>{" "}
                le temps : <strong>{mesure.offsetMs > 0 ? "+" : ""}{mesure.offsetMs} ms</strong> proposés.
              </p>
              <span className="reglage-aide">{mesure.frappesUtiles} frappes utiles</span>
              <button
                type="button"
                className="bouton"
                onClick={() => {
                  setSetting("audioOffsetMs", mesure.offsetMs);
                  setEtat("repos");
                }}
              >
                Appliquer {mesure.offsetMs > 0 ? "+" : ""}
                {mesure.offsetMs} ms
              </button>
            </>
          )}
          <button type="button" className="bouton discret" onClick={lancer}>
            Refaire le test
          </button>
          <button type="button" className="bouton discret" onClick={() => setEtat("repos")}>
            Laisser comme c'est
          </button>
        </div>
      ) : null}
    </div>
  );
}
